import React, { useState, useMemo } from "react";
import { Button, Center, Flex, Text, Box, Modal, ModalOverlay, ModalContent, ModalBody, Input, FormControl, FormLabel, Progress, VStack, HStack, Badge, Tooltip, IconButton } from "@chakra-ui/react";
import { useGame } from "../../contexts/GameContext";
import { EventTypes } from "types";
import { Draw } from "./Draw";
import { FaCheck, FaTimes } from "react-icons/fa";

const MAX_VOTES = 3;

export const Waiting: React.FC = () => {
  const { gameState, currentPlayer, sendEvent, isHost } = useGame();
  const { activePlayer } = gameState || {};
  const [showDrawModal, setShowDrawModal] = useState(false);
  const [customTopic, setCustomTopic] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [votingInProgress, setVotingInProgress] = useState<string | null>(null);
  const [vettingInProgress, setVettingInProgress] = useState<string | null>(null);

  // Get categories pending vetting (for host only)
  const pendingVettingCategories = useMemo(() => {
    const submittedCategories = gameState?.submittedCategories || {};
    return Object.entries(submittedCategories)
      .filter(([, data]) => data.vetted !== true) // Not yet vetted
      .map(([categoryId, data]) => ({
        categoryId,
        topic: data.topic,
        submitterName: data.submitterName,
      }));
  }, [gameState?.submittedCategories]);

  // Get all submitted categories for voting (excluding current player's submissions)
  // Only show vetted categories to non-hosts
  const votableCategories = useMemo(() => {
    const submittedCategories = gameState?.submittedCategories || {};
    const playerName = currentPlayer?.name || "";
    return Object.entries(submittedCategories)
      .filter(([, data]) => {
        // Don't show player their own submissions
        if (data.submitterName === playerName) return false;
        // Only show vetted categories for voting
        if (data.vetted !== true) return false;
        return true;
      })
      .map(([categoryId, data]) => {
        const votes = data.votes || [];
        const myVotesOnThis = votes.filter(v => v === playerName).length;
        return {
          categoryId,
          topic: data.topic,
          voteCount: votes.length,
          myVotes: myVotesOnThis,
        };
      })
      .sort((a, b) => b.voteCount - a.voteCount);
  }, [gameState?.submittedCategories, currentPlayer?.name]);

  // Get current player's submitted categories
  const mySubmittedCategories = useMemo(() => {
    const submittedCategories = gameState?.submittedCategories || {};
    return Object.entries(submittedCategories)
      .filter(([, data]) => data.submitterName === currentPlayer?.name)
      .map(([categoryId, data]) => ({
        categoryId,
        topic: data.topic,
        voteCount: (data.votes || []).length,
      }));
  }, [gameState?.submittedCategories, currentPlayer?.name]);

  // Count current player's total votes (including multiple votes on same category)
  const currentVoteCount = useMemo(() => {
    const submittedCategories = gameState?.submittedCategories || {};
    const playerName = currentPlayer?.name || "";
    let count = 0;
    for (const data of Object.values(submittedCategories)) {
      count += (data.votes || []).filter(v => v === playerName).length;
    }
    return count;
  }, [gameState?.submittedCategories, currentPlayer?.name]);

  const canVote = currentVoteCount < MAX_VOTES;

  // Check if all players' audio is ready (or if TTS is disabled)
  const playerAudioStatus = useMemo(() => {
    const players = gameState?.players || [];
    const audioProgress = gameState?.playerAudioProgress || {};
    const enableTTS = gameState?.enableTTS !== false; // Default to true

    if (!enableTTS || players.length === 0) {
      return { allReady: true, generating: 0, complete: 0, total: 0 };
    }

    let generating = 0;
    let complete = 0;

    players.forEach((player) => {
      const progress = audioProgress[player.name];
      if (progress?.status === 'complete') {
        complete++;
      } else if (progress?.status === 'generating') {
        generating++;
      }
    });

    return {
      allReady: complete === players.length,
      generating,
      complete,
      total: players.length,
    };
  }, [gameState?.players, gameState?.playerAudioProgress, gameState?.enableTTS]);

  // Check if game clue audio for Jeopardy round is ready
  const gameAudioStatus = useMemo(() => {
    const enableTTS = gameState?.enableTTS !== false;
    const ttsProgress = gameState?.ttsProgress;

    if (!enableTTS) {
      return { jeopardyReady: true, current: 0, total: 0, message: '' };
    }

    return {
      jeopardyReady: ttsProgress?.jeopardyRoundComplete ?? false,
      doubleJeopardyReady: ttsProgress?.doubleJeopardyRoundComplete ?? false,
      current: ttsProgress?.current ?? 0,
      total: ttsProgress?.total ?? 0,
      message: ttsProgress?.message ?? '',
    };
  }, [gameState?.enableTTS, gameState?.ttsProgress]);

  // Game is ready when player audio AND Jeopardy round clue audio are complete
  const isGameReady = playerAudioStatus.allReady && gameAudioStatus.jeopardyReady;

  const onStartGame = async () => {
    sendEvent({ type: EventTypes.START_GAME });
  };

  const onSubmitTopic = async () => {
    if (!customTopic.trim() || !currentPlayer) return;

    setIsSubmitting(true);
    try {
      console.log('[Waiting] Submitting custom topic:', customTopic);
      await sendEvent({
        type: EventTypes.SET_PLAYER_TOPIC,
        playerName: currentPlayer.name,
        topic: customTopic.trim(),
      });
      console.log('[Waiting] Topic submitted successfully');
      setCustomTopic(""); // Clear input after successful submission
    } catch (error) {
      console.error('[Waiting] Error submitting topic:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onVote = async (categoryId: string) => {
    if (!currentPlayer || votingInProgress) return;

    setVotingInProgress(categoryId);
    try {
      await sendEvent({
        type: EventTypes.VOTE_CATEGORY,
        voterName: currentPlayer.name,
        categoryId,
      });
    } catch (error) {
      console.error('[Waiting] Error voting:', error);
    } finally {
      setVotingInProgress(null);
    }
  };

  const onUnvote = async (categoryId: string) => {
    if (!currentPlayer || votingInProgress) return;

    setVotingInProgress(categoryId);
    try {
      await sendEvent({
        type: EventTypes.UNVOTE_CATEGORY,
        voterName: currentPlayer.name,
        categoryId,
      });
    } catch (error) {
      console.error('[Waiting] Error unvoting:', error);
    } finally {
      setVotingInProgress(null);
    }
  };

  const onVetCategory = async (categoryId: string) => {
    if (vettingInProgress) return;

    setVettingInProgress(categoryId);
    try {
      await sendEvent({
        type: EventTypes.VET_CATEGORY,
        categoryId,
      });
    } catch (error) {
      console.error('[Waiting] Error vetting category:', error);
    } finally {
      setVettingInProgress(null);
    }
  };

  const onRejectCategory = async (categoryId: string) => {
    if (vettingInProgress) return;

    setVettingInProgress(categoryId);
    try {
      await sendEvent({
        type: EventTypes.REJECT_CATEGORY,
        categoryId,
      });
    } catch (error) {
      console.error('[Waiting] Error rejecting category:', error);
    } finally {
      setVettingInProgress(null);
    }
  };

  // Get detailed progress for display
  const playerProgressList = useMemo(() => {
    const players = gameState?.players || [];
    const audioProgress = gameState?.playerAudioProgress || {};

    return players.map((player) => {
      const progress = audioProgress[player.name];
      return {
        name: player.name,
        current: progress?.current || 0,
        total: progress?.total || 9,
        status: progress?.status || 'pending',
      };
    });
  }, [gameState?.players, gameState?.playerAudioProgress]);

  return (
    <Center height="100%" width="100%" flexDir="column" gap="20px" p={4} overflowY="auto">
      {isHost ? (
        <Flex flexDirection="column" gap="16px" width="90%" maxWidth="400px" alignItems="center">
          <Text textAlign="center" textStyle="clue">
            You are the host! Start game when ready...
          </Text>

          {/* Audio generation progress */}
          {!isGameReady && (
            <Box
              width="100%"
              bg="rgba(0,0,0,0.4)"
              p={4}
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Text textStyle="clue" fontSize="sm" mb={3} textAlign="center">
                Generating Voice Audio
              </Text>
              <VStack spacing={3} width="100%">
                {/* Player audio progress */}
                {playerAudioStatus.total > 0 && playerProgressList.map((player) => (
                  <Box key={player.name} width="100%">
                    <HStack justify="space-between" mb={1}>
                      <Text textStyle="clue" fontSize="xs">
                        {player.name}
                      </Text>
                      <Text textStyle="clue" fontSize="xs" opacity={0.7}>
                        {player.status === 'complete' ? '✓' : `${player.current}/${player.total}`}
                      </Text>
                    </HStack>
                    <Progress
                      value={(player.current / player.total) * 100}
                      size="sm"
                      colorScheme={player.status === 'complete' ? 'green' : 'yellow'}
                      bg="whiteAlpha.200"
                      borderRadius="full"
                      hasStripe={player.status === 'generating'}
                      isAnimated={player.status === 'generating'}
                    />
                  </Box>
                ))}

                {/* Game clue audio progress */}
                {gameAudioStatus.total > 0 && (
                  <Box width="100%">
                    <HStack justify="space-between" mb={1}>
                      <Text textStyle="clue" fontSize="xs">
                        Clue Audio
                      </Text>
                      <Text textStyle="clue" fontSize="xs" opacity={0.7}>
                        {gameAudioStatus.jeopardyReady ? '✓ Round 1 Ready' : gameAudioStatus.message || `${gameAudioStatus.current}/${gameAudioStatus.total}`}
                      </Text>
                    </HStack>
                    <Progress
                      value={(gameAudioStatus.current / gameAudioStatus.total) * 100}
                      size="sm"
                      colorScheme={gameAudioStatus.jeopardyReady ? 'green' : 'blue'}
                      bg="whiteAlpha.200"
                      borderRadius="full"
                      hasStripe={!gameAudioStatus.jeopardyReady}
                      isAnimated={!gameAudioStatus.jeopardyReady}
                    />
                  </Box>
                )}
              </VStack>
            </Box>
          )}

          {/* All ready indicator */}
          {isGameReady && (
            <Box
              width="100%"
              bg="green.600"
              p={3}
              borderRadius="md"
              textAlign="center"
            >
              <Text textStyle="clue" fontSize="sm">
                ✓ Ready to play!
              </Text>
            </Box>
          )}

          <Button
            onClick={onStartGame}
            isDisabled={!isGameReady}
            width="100%"
            size="lg"
            colorScheme={isGameReady ? "yellow" : "gray"}
          >
            {isGameReady ? "Start Game" : "Waiting for audio..."}
          </Button>

          {/* Category Vetting Section for Host */}
          {pendingVettingCategories.length > 0 && (
            <Box
              width="100%"
              bg="rgba(0,0,0,0.4)"
              p={4}
              borderRadius="lg"
              border="1px solid"
              borderColor="orange.400"
            >
              <HStack justify="space-between" mb={3}>
                <Text textStyle="clue" fontSize="sm" color="orange.300">
                  Categories Pending Approval
                </Text>
                <Badge colorScheme="orange">
                  {pendingVettingCategories.length} pending
                </Badge>
              </HStack>
              <VStack spacing={2} width="100%">
                {pendingVettingCategories.map(({ categoryId, topic, submitterName }) => (
                  <Box
                    key={categoryId}
                    width="100%"
                    p={3}
                    borderRadius="md"
                    bg="whiteAlpha.100"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                  >
                    <HStack justify="space-between" align="center">
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontSize="sm" fontWeight="bold" color="white">
                          {topic}
                        </Text>
                        <Text fontSize="xs" color="whiteAlpha.600">
                          by {submitterName}
                        </Text>
                      </VStack>
                      <HStack spacing={2}>
                        <Tooltip label="Approve for voting">
                          <IconButton
                            aria-label="Approve category"
                            icon={<FaCheck />}
                            size="sm"
                            colorScheme="green"
                            onClick={() => onVetCategory(categoryId)}
                            isLoading={vettingInProgress === categoryId}
                          />
                        </Tooltip>
                        <Tooltip label="Reject category">
                          <IconButton
                            aria-label="Reject category"
                            icon={<FaTimes />}
                            size="sm"
                            colorScheme="red"
                            onClick={() => onRejectCategory(categoryId)}
                            isLoading={vettingInProgress === categoryId}
                          />
                        </Tooltip>
                      </HStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
              <Text fontSize="xs" color="gray.400" mt={2} textAlign="center">
                Approved categories will appear for players to vote on
              </Text>
            </Box>
          )}
        </Flex>
      ) : (
        <Text textStyle="clue">
          Waiting for {activePlayer?.name || "host"} to start the game
        </Text>
      )}

      {currentPlayer && (
        <>
          <Button
            colorScheme="yellow"
            size="lg"
            onClick={() => setShowDrawModal(true)}
            mt={2}
          >
            {currentPlayer.nameSvg ? "Redraw Your Name" : "Draw Your Name"}
          </Button>

          <Modal
            isOpen={showDrawModal}
            onClose={() => setShowDrawModal(false)}
            size="full"
          >
            <ModalOverlay />
            <ModalContent bg="jeopardy.blue" height="100vh">
              <ModalBody p={0} height="100%">
                <Draw onComplete={() => setShowDrawModal(false)} />
              </ModalBody>
            </ModalContent>
          </Modal>
        </>
      )}

      {/* Category Submission Section - Always show form */}
      <Box mt={2} width="90%" maxWidth="400px">
        <FormControl>
          <FormLabel textStyle="clue" color="white" fontSize="sm">
            Submit Categories for Double Jeopardy
          </FormLabel>
          <HStack>
            <Input
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              backgroundColor="white"
              placeholder="e.g., 90s Hip Hop, Marine Biology"
              maxLength={50}
              size="sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && customTopic.trim()) {
                  onSubmitTopic();
                }
              }}
            />
            <Button
              colorScheme="blue"
              onClick={onSubmitTopic}
              isLoading={isSubmitting}
              isDisabled={!customTopic.trim()}
              size="sm"
            >
              Add
            </Button>
          </HStack>
          <Text fontSize="xs" color="gray.300" mt={1}>
            Top voted categories will appear in Double Jeopardy!
          </Text>
        </FormControl>

        {/* Show player's own submissions */}
        {mySubmittedCategories.length > 0 && (
          <Box mt={3}>
            <Text fontSize="xs" color="whiteAlpha.700" mb={1}>
              Your submissions:
            </Text>
            <VStack spacing={1} align="stretch">
              {mySubmittedCategories.map(({ categoryId, topic, voteCount }) => (
                <HStack
                  key={categoryId}
                  justify="space-between"
                  bg="blue.700"
                  px={2}
                  py={1}
                  borderRadius="md"
                >
                  <Text fontSize="sm" color="white">
                    {topic}
                  </Text>
                  <Badge colorScheme="yellow" fontSize="xs">
                    {voteCount} {voteCount === 1 ? 'vote' : 'votes'}
                  </Badge>
                </HStack>
              ))}
            </VStack>
          </Box>
        )}
      </Box>

      {/* Category Voting Section */}
      {votableCategories.length > 0 && (
        <Box mt={2} width="90%" maxWidth="400px">
          <HStack justify="space-between" mb={2}>
            <Text textStyle="clue" fontSize="sm" color="white">
              Vote for Categories
            </Text>
            <Badge colorScheme={canVote ? "blue" : "gray"}>
              {currentVoteCount}/{MAX_VOTES} votes
            </Badge>
          </HStack>
          
          <VStack spacing={2} width="100%">
            {votableCategories.map(({ categoryId, topic, voteCount, myVotes }) => (
              <Box
                key={categoryId}
                width="100%"
                p={3}
                borderRadius="md"
                bg={myVotes > 0 ? "green.700" : "whiteAlpha.200"}
                border="1px solid"
                borderColor={myVotes > 0 ? "green.400" : "whiteAlpha.300"}
              >
                <HStack justify="space-between" align="center">
                  <Text fontSize="sm" fontWeight="bold" color="white" flex={1}>
                    {topic}
                  </Text>
                  <HStack spacing={2}>
                    <Badge colorScheme="yellow" fontSize="xs">
                      {voteCount} {voteCount === 1 ? 'vote' : 'votes'}
                    </Badge>
                    <HStack spacing={1}>
                      <Tooltip
                        label="Remove a vote"
                        isDisabled={myVotes === 0}
                      >
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant={myVotes > 0 ? "solid" : "outline"}
                          onClick={() => onUnvote(categoryId)}
                          isDisabled={myVotes === 0}
                          isLoading={votingInProgress === categoryId}
                          minW="32px"
                        >
                          -
                        </Button>
                      </Tooltip>
                      {myVotes > 0 && (
                        <Badge colorScheme="green" fontSize="xs" minW="20px" textAlign="center">
                          {myVotes}
                        </Badge>
                      )}
                      <Tooltip
                        label={!canVote ? "You've used all your votes" : "Add a vote"}
                        isDisabled={canVote}
                      >
                        <Button
                          size="sm"
                          colorScheme="green"
                          onClick={() => onVote(categoryId)}
                          isDisabled={!canVote}
                          isLoading={votingInProgress === categoryId}
                          minW="32px"
                        >
                          +
                        </Button>
                      </Tooltip>
                    </HStack>
                  </HStack>
                </HStack>
              </Box>
            ))}
          </VStack>
          
          <Text fontSize="xs" color="gray.400" mt={2} textAlign="center">
            Top voted categories replace Double Jeopardy!
          </Text>
        </Box>
      )}
    </Center>
  );
};
